import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useWarbandStore } from '../../stores/warbandStore';
import { Spell } from '../../types';
import { SpellCard } from '../shared/SpellCard';
import { SpellSchoolChip } from '../shared/SpellSchoolChip';

interface SelectSpellDialogProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  onSelect: (spell: Spell) => void;
  /** Spell pool to choose from. Defaults to all available spells from the store. */
  spells?: Spell[];
}

export const SelectSpellDialog: React.FC<SelectSpellDialogProps> = ({
  open,
  title = 'Select Spell',
  onClose,
  onSelect,
  spells: spellsProp,
}) => {
  const allAvailableSpells = useWarbandStore((s) => s.allAvailableSpells);

  const [selectedSpellId, setSelectedSpellId] = React.useState('');
  const [selectedSchool, setSelectedSchool] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const spells = spellsProp ?? allAvailableSpells;

  const availableSchools = React.useMemo(() => {
    const schools = new Set<string>();
    spells.forEach((spell) => schools.add(spell.school?.trim() || 'Unknown'));
    return Array.from(schools).sort((a, b) => a.localeCompare(b));
  }, [spells]);

  // Keep school selection in sync
  React.useEffect(() => {
    setSelectedSchool((prev) => (availableSchools.includes(prev) ? prev : availableSchools[0] || ''));
  }, [availableSchools]);

  React.useEffect(() => {
    if (!open) {
      setSelectedSpellId('');
      setSelectedSchool('');
      setError(null);
    }
  }, [open]);

  const visibleSpells = React.useMemo(
    () =>
      spells
        .filter((s) => (s.school?.trim() || 'Unknown') === selectedSchool)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [spells, selectedSchool]
  );

  const selectedSpell = React.useMemo(
    () => spells.find((s) => s.id === selectedSpellId) ?? null,
    [spells, selectedSpellId]
  );

  const handleConfirm = async () => {
    if (!selectedSpell) return;
    setLoading(true);
    setError(null);
    try {
      onSelect(selectedSpell);
      onClose();
    } catch {
      setError('Failed to select spell. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, height: 440 }}>
        <Typography variant="h6">{title}</Typography>
        {error && <Alert severity="error">{error}</Alert>}

        {spells.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No spells available.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="select-spell-school-label">School</InputLabel>
              <Select
                labelId="select-spell-school-label"
                value={selectedSchool}
                label="School"
                onChange={(e) => {
                  setSelectedSchool(e.target.value);
                  setSelectedSpellId('');
                }}
              >
                {availableSchools.map((school) => (
                  <MenuItem key={school} value={school}>
                    <SpellSchoolChip label={school} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {visibleSpells.map((spell) => (
                <Chip
                  key={spell.id}
                  label={spell.name}
                  clickable
                  variant={selectedSpellId === spell.id ? 'filled' : 'outlined'}
                  color={selectedSpellId === spell.id ? 'primary' : 'default'}
                  onClick={() => setSelectedSpellId(spell.id)}
                />
              ))}
            </Box>

            {selectedSpell ? (
              <SpellCard spell={selectedSpell} />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2, flexGrow: 1 }}
              >
                Select a spell to view details
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedSpellId || loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Select'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
